// Deck project math: boards, joists, posts, concrete, screws.
// Board counts assume 12-ft deck boards (most common stocked length).
// Joist spacing: 16 in on-center. Post spacing: every 8 ft along perimeter.
import { ceilTo, round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export type DeckBoardWidth = 3.5 | 5.5  // actual inches (2×4 actual = 3.5, 2×6 actual = 5.5)

export interface DeckInputs {
  deckLengthFt: number
  deckWidthFt: number
  boardWidthIn: DeckBoardWidth  // actual board width in inches
  boardSpacingIn: number        // gap between boards, default 0.25
  wastePct: number              // 10-20, default 15
  needRailing: boolean
  railingLinFt: number          // linear feet of railing (if needRailing)
  useHiddenFasteners: boolean
}

export interface DeckResult {
  deckAreaSqFt: number
  deckBoardsLinFt: number      // total linear feet of decking needed
  deckBoardPieces: number      // pieces at 12 ft
  joistCount: number
  postCount: number
  concreteBags60lb: number     // 2 per post
  deckScrewLbs: number         // 1 lb per 100 sq ft
  hiddenFastenersQty: number   // if useHiddenFasteners: ~80 per 100 sq ft
  railingPosts: number         // if needRailing: 1 per 4 lin ft
  lines: AccessoryLine[]
}

export const DEFAULT_DECK: DeckInputs = {
  deckLengthFt: 16,
  deckWidthFt: 12,
  boardWidthIn: 5.5,
  boardSpacingIn: 0.25,
  wastePct: 15,
  needRailing: true,
  railingLinFt: 40,
  useHiddenFasteners: false,
}

const BOARD_LENGTH_FT = 12      // standard stocked deck board length
const JOIST_SPACING_FT = 16 / 12  // 16 in o.c. = 1.333 ft
const POST_SPACING_FT = 8       // one post every 8 ft of perimeter / 2 sides
const CONCRETE_PER_POST = 2     // 60 lb bags per post hole
const FASTENERS_PER_100 = 80    // hidden fasteners per 100 sq ft
const RAILING_POST_SPACING = 4  // 1 post per 4 lin ft

export function computeDeck(i: DeckInputs, locale: Locale = 'en'): DeckResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  const lFt = Math.max(0, i.deckLengthFt)
  const wFt = Math.max(0, i.deckWidthFt)
  const deckAreaSqFt = lFt * wFt

  // Board rows across deck width (board + gap)
  const boardPlusGap = (Math.max(1, i.boardWidthIn) + Math.max(0, i.boardSpacingIn)) / 12
  const rows = deckAreaSqFt > 0 ? Math.ceil(wFt / boardPlusGap) : 0

  // Linear feet of boards needed
  const deckBoardsLinFt = deckAreaSqFt > 0
    ? rows * lFt * (1 + Math.max(0, i.wastePct) / 100)
    : 0

  // 12-ft pieces
  const deckBoardPieces = deckAreaSqFt > 0 ? ceilTo(deckBoardsLinFt / BOARD_LENGTH_FT, 1) : 0

  // Joists along the length: floor(length / 1.333) + 1 (both end joists)
  const joistCount = deckAreaSqFt > 0 ? Math.floor(lFt / JOIST_SPACING_FT) + 1 : 0

  // Posts: one at each corner + additional posts every 8 ft along both long sides
  const postsLongSide = lFt > 0 ? Math.ceil(lFt / POST_SPACING_FT) + 1 : 0
  const postCount = deckAreaSqFt > 0 ? Math.max(4, postsLongSide * 2) : 0

  const concreteBags60lb = postCount > 0 ? postCount * CONCRETE_PER_POST : 0

  // Screws: 1 lb per 100 sq ft (standard deck screws, 3 in)
  const deckScrewLbs = deckAreaSqFt > 0 ? Math.max(1, ceilTo(deckAreaSqFt / 100, 1)) : 0

  const hiddenFastenersQty = i.useHiddenFasteners && deckAreaSqFt > 0
    ? ceilTo((deckAreaSqFt / 100) * FASTENERS_PER_100, 1)
    : 0

  const railingPosts = i.needRailing && i.railingLinFt > 0
    ? Math.max(2, ceilTo(i.railingLinFt / RAILING_POST_SPACING, 1))
    : 0

  const lines: AccessoryLine[] = []

  lines.push({
    item: 'joist hanger double 2x8 galvanized',
    label: tr('Joist hangers', 'Étriers à solive'),
    kind: 'consumable',
    qty: joistCount > 0 ? joistCount * 2 : 0,
    unit: 'hanger',
    note: tr('2 hangers per joist · match hanger size to joist lumber size', '2 étriers par solive · adaptez la taille de l’étrier à celle de la solive'),
  })

  lines.push({
    item: 'structural screws structural wood screws',
    label: tr('Structural screws', 'Vis de structure'),
    kind: 'consumable',
    qty: Math.max(1, ceilTo(joistCount * 6, 100)),
    unit: 'screw',
    note: tr('Structural screws for ledger, headers and post connections — use code-approved connectors', 'Vis de structure pour la lambourde murale, les sommiers et les raccords de poteaux — utilisez des connecteurs approuvés par le code'),
  })

  lines.push({
    item: 'concrete mix 60 lb bag quikrete',
    label: tr('Concrete bags (60 lb)', 'Sacs de béton (60 lb)'),
    kind: 'consumable',
    qty: concreteBags60lb,
    unit: 'bag',
    note: tr('2 bags per post hole · mix in the hole dry or pre-mix · allow 24 h before framing', '2 sacs par trou de poteau · mélangez à sec dans le trou ou pré-mélangez · attendez 24 h avant la charpente'),
  })

  lines.push({
    item: 'deck post base adjustable standoff',
    label: tr('Post bases', 'Ancrages de poteau'),
    kind: 'consumable',
    qty: postCount,
    unit: 'base',
    note: tr('Standoff post bases keep wood off concrete and are required by most codes', 'Les ancrages de poteau surélevés gardent le bois hors du béton et sont exigés par la plupart des codes'),
  })

  if (i.useHiddenFasteners) {
    lines.push({
      item: 'hidden deck fasteners clips ipe composite',
      label: tr('Hidden fasteners', 'Attaches dissimulées'),
      kind: 'consumable',
      qty: hiddenFastenersQty,
      unit: 'fastener',
      note: tr(`~${FASTENERS_PER_100} per 100 sq ft · check system spec for exact count`, `~${FASTENERS_PER_100} par 100 pi² · vérifiez la fiche du système pour le compte exact`),
    })
  } else {
    lines.push({
      item: 'deck screws stainless 3 inch box',
      label: tr('Deck screws', 'Vis de terrasse'),
      kind: 'consumable',
      qty: deckScrewLbs,
      unit: 'lb',
      note: tr('3 in coarse-thread deck screws · ~1 lb per 100 sq ft · use stainless for cedar/redwood', 'Vis de terrasse à filet grossier de 3 po · ~1 lb par 100 pi² · utilisez de l’inox pour le cèdre/séquoia'),
    })
  }

  if (i.needRailing && railingPosts > 0) {
    lines.push({
      item: '4x4 railing post deck baluster post',
      label: tr('Railing posts', 'Poteaux de garde-corps'),
      kind: 'consumable',
      qty: railingPosts,
      unit: 'post',
      note: tr(`1 post per ${RAILING_POST_SPACING} lin ft · typically 4×4 at 36 in height`, `1 poteau par ${RAILING_POST_SPACING} pi linéaires · habituellement 4×4 à 36 po de hauteur`),
    })
    lines.push({
      item: 'deck railing kit balusters top rail',
      label: tr('Rail kit (top + bottom rail)', 'Trousse de garde-corps (lisse haute + basse)'),
      kind: 'consumable',
      qty: Math.max(1, ceilTo(i.railingLinFt / 8, 1)),
      unit: 'section',
      note: tr('8 ft sections · includes top rail, bottom rail and balusters', 'Sections de 8 pi · comprend la lisse haute, la lisse basse et les barreaux'),
    })
  }

  lines.push({ item: 'deck wood stain sealer exterior', label: tr('Deck stain / sealer', 'Teinture / scellant pour terrasse'), kind: 'consumable', qty: Math.max(1, ceilTo(deckAreaSqFt / 250, 1)), unit: 'gal', note: tr('~250 sq ft per gallon · apply first season after install', '~250 pi² par gallon · appliquez la première saison après la pose'), optional: true })
  lines.push({ item: 'circular saw blade framing', label: tr('Circular saw', 'Scie circulaire'), kind: 'tool', note: tr('For cutting boards to length and ripping if needed.', 'Pour couper les planches à longueur et les refendre au besoin.') })
  lines.push({ item: 'post hole digger clamshell manual', label: tr('Post hole digger', 'Tarière à main'), kind: 'tool', note: tr('Manual clamshell for small jobs; rent a gas auger for many posts.', 'Tarière à main pour les petits travaux; louez une tarière à essence pour plusieurs poteaux.'), optional: true })
  lines.push({ item: 'drill impact driver set', label: tr('Impact driver', 'Tournevis à chocs'), kind: 'tool', note: tr('A corded or brushless impact is far faster than a standard drill for deck screws.', 'Un tournevis à chocs (filaire ou sans balais) est bien plus rapide qu’une perceuse ordinaire pour les vis de terrasse.') })

  return {
    deckAreaSqFt: round(deckAreaSqFt, 0),
    deckBoardsLinFt: round(deckBoardsLinFt, 0),
    deckBoardPieces,
    joistCount,
    postCount,
    concreteBags60lb,
    deckScrewLbs,
    hiddenFastenersQty,
    railingPosts,
    lines,
  }
}
